// Audit log table: who did what and when. Used by the admin activity page
// and a questionnaire's own activity card.
import { History, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../api/client';
import type { AuditEntry, AuditPage } from '../api/types';
import { formatDate } from '../format';
import { useI18n, type MessageKey } from '../i18n';
import { Empty } from './ui';

const PAGE = 50;

/** Readable action name; unknown actions fall back to the raw code. */
export function actionLabel(action: string, t: (k: MessageKey) => string): string {
  const key = `audit.${action}` as MessageKey;
  const label = t(key);
  return label === key ? action : label;
}

function detailText(e: AuditEntry): string {
  return Object.entries(e.details ?? {})
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
    .join(' · ');
}

export function AuditList({ load, showSurvey }: { load: (p: { action?: string; actor?: string; offset: number; limit: number }) => Promise<AuditPage>; showSurvey?: boolean }) {
  const { t } = useI18n();
  const [action, setAction] = useState('');
  const [actor, setActor] = useState('');
  const [page, setPage] = useState<AuditPage | null>(null);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState('');
  useEffect(() => {
    const timer = setTimeout(
      () =>
        load({ action: action || undefined, actor: actor || undefined, offset, limit: PAGE }).then(
          (p) => setPage((prev) => (offset && prev ? { ...p, entries: [...prev.entries, ...p.entries] } : p)),
          (e) => setError(e instanceof ApiError ? e.message : t('common.load_failed')),
        ),
      200,
    );
    return () => clearTimeout(timer);
    // load is stable per caller
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, actor, offset]);
  const groups = ['auth.', 'survey.', 'response', 'user.', 'library.', 'respondent.', 'account.'];
  return (
    <div className="stack gap-12">
      <div className="row gap-8 wrap">
        <select className="select" style={{ width: 'auto' }} value={action} onChange={(e) => (setOffset(0), setAction(e.target.value))} aria-label={t('audit.filter_action')}>
          <option value="">{t('audit.all_actions')}</option>
          {groups.map((g) => (
            <option key={g} value={g}>
              {t(`audit.group.${g.replace('.', '')}` as MessageKey)}
            </option>
          ))}
        </select>
        {showSurvey && (
          <label className="search grow" style={{ maxWidth: 320 }}>
            <Search aria-hidden />
            <input className="input" value={actor} placeholder={t('audit.filter_actor')} aria-label={t('audit.filter_actor')} onChange={(e) => (setOffset(0), setActor(e.target.value))} />
          </label>
        )}
      </div>
      {error && <div className="banner error">{error}</div>}
      {page && page.entries.length === 0 ? (
        <Empty icon={<History />} title={t('audit.empty')} />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>{t('audit.when')}</th>
                <th>{t('audit.who')}</th>
                <th>{t('audit.what')}</th>
                {showSurvey && <th>{t('audit.questionnaire')}</th>}
                <th>{t('audit.details')}</th>
              </tr>
            </thead>
            <tbody>
              {page?.entries.map((e) => (
                <tr key={e.id}>
                  <td className="nowrap">{formatDate(e.at, true)}</td>
                  <td>
                    {e.actorEmail || <span className="muted">{t('common.anonymous')}</span>}
                    {e.ip && <div className="small muted">{e.ip}</div>}
                  </td>
                  <td>
                    <span className={`pill ${e.action.endsWith('failed') || e.action.endsWith('delete') ? 'red' : ''}`}>{actionLabel(e.action, t)}</span>
                    {e.target && <div className="small muted ellipsis" style={{ maxWidth: 260 }}>{e.target}</div>}
                  </td>
                  {showSurvey && <td>{e.surveyId && e.surveyTitle ? <Link to={`/surveys/${e.surveyId}`}>{e.surveyTitle}</Link> : <span className="muted">—</span>}</td>}
                  <td className="small muted">{detailText(e)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {page && page.entries.length < page.total && (
        <div>
          <button className="btn sm" onClick={() => setOffset(page.entries.length)}>
            {t('audit.more', { count: page.total - page.entries.length })}
          </button>
        </div>
      )}
    </div>
  );
}
